import { JxsComponent } from '@jxs/component';
import { JxsScrollTrigger, gsap } from '@jxs/gsap';
import { clamp } from '@jxs/utilities';

const COMPONENT_NAME = 'jxs-st-section';

/**
 * @typedef {Object} JxsStSectionRefs
 * @property {HTMLElement} viewport
 * @property {HTMLElement} track
 * @property {HTMLElement[]} [items]
 */

/** @extends {JxsComponent<JxsStSectionRefs>} */
class JxsStSection extends JxsComponent {

  /**
   * @param {number} progress
   */
  #onProgress = (progress) => {
    if (this.itemTotal <= 1) return;

    this.applyPanProgress(this.track, progress);
  }

  /**
   * @param {number} index
   */
  #onIndexChange = (index) => {
    if (this.itemTotal <= 1) return;

    this.applySwitchIndex(this.track, index);
  }

  /**
   * @param {boolean} disabled 
   */
  #onDisabledChange = (disabled) => {
    this.setAttribute('pin-disabled', disabled + '')
    if (!disabled) return;

    gsap.killTweensOf(this.track);
    gsap.set(this.track, {
      clearProps: 'transform',
    });
  }

  constructor() {
    super();

    this.scroller = null;
    this.activeIndex = -1;
    this.itemTotal = 0;
  }

  connectedCallback() {
    super.connectedCallback();
    this.init();
  }

  updatedCallback() {
    super.updatedCallback();
    this.init();
  }

  disconnectedCallback() {
    this.destroy();
    super.disconnectedCallback();
  }

  /**
   * @returns {HTMLElement}
   */
  get viewport() {
    return this.refs.viewport;
  }

  /**
   * @returns {HTMLElement}
   */
  get track() {
    return this.refs.track;
  }

  /**
   * @returns {HTMLElement[]}
   */
  get items() {
    return this.refs.items ?? [];
  }

  init() {

    this.destroy();

    const viewport = this.viewport;
    const track = this.track;
    const items = this.items;

    if (items.length <= 1) {
      this.setActiveIndex(0);
      return;
    }

    this.itemTotal = items.length;

    gsap.set(track, {
      x: 0,
      force3D: true,
    });

    this.setActiveIndex(0);

    this.scroller = new JxsScrollTrigger(viewport, {
      trigger: viewport,
      itemTotal: this.itemTotal,
      startHold: this.getDataset('translateMode') === 'pan' ? 200 : 0,
      endHold: 500
    });

    if (this.getDataset('translateMode') === 'switch') {
      this.scroller.on('indexChange', this.#onIndexChange);
    } else {
      this.scroller.on('progress', this.#onProgress);
    }
  }

  destroy() {
    if (this.scroller) {
      this.scroller.destroy();
      this.scroller = null;
    }

    gsap.set(this.track, {
      clearProps: 'transform',
    });

    this.activeIndex = -1;
  }

  /**
   * @param {HTMLElement} track
   * @param {number} progress
   */
  applyPanProgress(track, progress) {
    const x = -this.getMaxTranslateX() * progress;

    gsap.set(track, {
      x,
      force3D: true,
    });

    this.setActiveIndex(Math.round(progress * (this.itemTotal - 1)));
  }

  /**
   * @param {HTMLElement} track
   * @param {number} index
   */
  applySwitchIndex(track, index) {
    const safeIndex = clamp(index, 0, this.itemTotal - 1, 0);
    const x = -this.getItemTranslateX(safeIndex);

    gsap.to(track, {
      x,
      duration: 0.35,
      ease: 'power2.out',
      overwrite: true,
      force3D: true,
    });

    this.setActiveIndex(safeIndex);
  }

  getMaxTranslateX() {
    return this.getItemTranslateX(this.itemTotal - 1);
  }

  /**
   * @param {number} index
   * @returns {number}
   */
  getItemTranslateX(index) {
    return this.viewport.offsetWidth * index;
  }

  /**
   * @param {number} index
   */
  setActiveIndex(index) {
    const items = this.items;
    const nextIndex = clamp(index, 0, Math.max(items.length - 1, 0), 0);

    if (nextIndex === this.activeIndex) return;

    this.activeIndex = nextIndex;

    items.forEach((item, itemIndex) => {
      const isActive = itemIndex === nextIndex;

      item.classList.toggle('is-active', isActive);
      item.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    });

    this.style.setProperty('--jxs-st-section-active-index', String(nextIndex));
  }
}

if (!customElements.get(COMPONENT_NAME)) {
  customElements.define(COMPONENT_NAME, JxsStSection);
}